import React from "react";

/**
 * Inbox markdown — STRICT subset renderer.
 *
 * The inbox composer (tenant + admin) lets authors write a small, safe
 * subset of markdown: paragraphs, line breaks, **bold**, *italic*, and
 * inline [text](https://link) where the URL must be http(s) or mailto.
 * Anything else is rendered as plain text. We deliberately avoid a full
 * markdown library to keep the bundle small and the attack surface
 * narrow — the parser below is hand-rolled and only emits React elements
 * (never dangerouslySetInnerHTML), which means there is no string→HTML
 * round-trip the browser could be tricked into mis-parsing.
 *
 * URL safety:
 *   • Only http://, https://, mailto: prefixes are allowed.
 *   • Anything else (javascript:, data:, file:, vbscript:, …) renders
 *     as the link text without an anchor — defence-in-depth on top of
 *     SafeUrlSchema on the server.
 *
 * See DECISIONS.md (D50) for why we do markdown subset + hand-rolled
 * sanitizer instead of marked + DOMPurify.
 */

const SAFE_URL = /^(?:https?:|mailto:)/i;

function isSafeUrl(href: string): boolean {
  return SAFE_URL.test(href.trim());
}

/**
 * Tokenize a single line into inline parts: text, bold, italic, link.
 * The order of regex application matters — we run link first so that
 * a link's display text isn't itself italicised by the italic pass.
 */
function renderInline(text: string, keyBase: string): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  // Match link first; everything outside the link still goes through
  // bold/italic.
  const linkRe = /\[([^\]]+)\]\(([^)\s]+)\)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let i = 0;
  while ((match = linkRe.exec(text)) !== null) {
    if (match.index > lastIndex) {
      out.push(
        ...renderEmphasis(
          text.slice(lastIndex, match.index),
          `${keyBase}-t${i}`,
        ),
      );
    }
    const [, label, href] = match;
    if (isSafeUrl(href)) {
      out.push(
        <a
          key={`${keyBase}-a${i}`}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-navy underline underline-offset-2 hover:text-navy/80"
        >
          {label}
        </a>,
      );
    } else {
      // Unsafe URL — render the label as plain text. This is the
      // primary defence against javascript: / data: payloads slipping
      // through if a future server bug ever lets them past the Zod
      // SafeUrlSchema.
      out.push(<span key={`${keyBase}-u${i}`}>{label}</span>);
    }
    lastIndex = match.index + match[0].length;
    i += 1;
  }
  if (lastIndex < text.length) {
    out.push(...renderEmphasis(text.slice(lastIndex), `${keyBase}-t${i}`));
  }
  return out;
}

/**
 * Emit React nodes for **bold** and *italic*. Bold is matched first;
 * its regex requires double-asterisks so the italic single-asterisk
 * pass cannot collide.
 */
function renderEmphasis(text: string, keyBase: string): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  const re = /\*\*([^*]+)\*\*|\*([^*]+)\*/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let i = 0;
  while ((match = re.exec(text)) !== null) {
    if (match.index > lastIndex) {
      out.push(text.slice(lastIndex, match.index));
    }
    if (match[1] !== undefined) {
      out.push(
        <strong key={`${keyBase}-b${i}`} className="font-semibold">
          {match[1]}
        </strong>,
      );
    } else {
      out.push(<em key={`${keyBase}-i${i}`}>{match[2]}</em>);
    }
    lastIndex = match.index + match[0].length;
    i += 1;
  }
  if (lastIndex < text.length) {
    out.push(text.slice(lastIndex));
  }
  return out;
}

export function InboxMarkdown({ source }: { source: string }) {
  // Split into paragraphs on blank lines; within a paragraph, single
  // line breaks become <br /> so the author's intent survives.
  const paragraphs = source
    .replace(/\r\n/g, "\n")
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter((p) => p.length > 0);
  return (
    <div
      className="space-y-3 text-sm leading-relaxed text-navy/90"
      data-testid="inbox-markdown"
    >
      {paragraphs.map((para, idx) => {
        const lines = para.split("\n");
        return (
          <p key={`p${idx}`}>
            {lines.map((line, lIdx) => (
              <React.Fragment key={`p${idx}-l${lIdx}`}>
                {renderInline(line, `p${idx}-l${lIdx}`)}
                {lIdx < lines.length - 1 && <br />}
              </React.Fragment>
            ))}
          </p>
        );
      })}
    </div>
  );
}
